<!-- @format -->

<template>
	<disabled-text v-if="disabled && pcProps.showDisabledText" :value="value" :params="params" />
	<el-select
		v-else
		v-model="value"
		:disabled="disabled"
		clearable
		:filterable="!pcProps.isNotfilterable"
		:remote="pcProps.isRemote"
		default-first-option
		placeholder="请选择下拉数据"
		class="select"
		:remote-method="remoteMethod"
		@change="handelChange"
	>
		<el-option v-for="item in params" :key="item.label" :label="item.label" :value="item.value" />
	</el-select>
</template>

<script setup lang="ts">
	import { paramsTypeEnum } from '../../config';
	import { type WidgetConfigType } from '../../types';
	import { getCheckRowsToFormDatas } from '../../utils/user';
	import { flat } from '../../utils/util';
	import Script from '../../utils/script';
	import DisabledText from './disabledText.vue';
	import { useDictList } from '@/api';
	import { getCache, setCache } from '@/utils/util';
	import { getDataProcessList } from '@/api/factory';

	interface Props {
		modelValue: string;
		pcProps: WidgetConfigType;
		disabled?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		modelValue: '',
	});
	const widgetDatas = inject<any>('widgetDatas', ref([]));
	const emit = defineEmits(['update:modelValue']);
	const flatWigetDatas = computed(() => flat(widgetDatas.value || []));
	const attr = useAttrs();
	const setFieldsValue = inject<(fields: Record<string, any>) => void>('setFieldsValue', () => {});
	const scriptConfig: Script | undefined = inject('scriptConfig', undefined);

	const value = computed({
		get() {
			if (attr.multiple) {
				return props.modelValue ? props.modelValue.split(',') : [];
			} else {
				return props.modelValue;
			}
		},
		set(val) {
			emit('update:modelValue', val ? val?.toString() : '');
		},
	});

	const dictList = ref<any>([]);

	const params = computed(() =>
		props.pcProps.paramsType &&
		(props.pcProps.paramsType === paramsTypeEnum.引用 || props.pcProps.paramsType === paramsTypeEnum.业务对象)
			? dictList.value
			: props.pcProps.params,
	);

	const getDictList = async (dictType: string) => {
		const cacheRes = getCache('dictCache', dictType);
		if (cacheRes) {
			dictList.value = cacheRes;
		} else {
			const res = await useDictList({ dictType });
			setCache('dictCache', dictType, res);
			dictList.value = res;
		}
	};

	const getDataProcessListData = async (newObjectType, newObjectServer) => {
		const res: any = await getDataProcessList({
			businessCode: newObjectType,
			url: newObjectServer,
		});
		dictList.value = res.map((item) => ({
			...item,
			value: props.pcProps.objectValue ? item[props.pcProps.objectValue] : '',
			label: props.pcProps.objectLabel ? item[props.pcProps.objectLabel] : '',
		}));
	};

	const handelChange = (val: any) => {
		if (props.pcProps.paramsType === paramsTypeEnum.业务对象) {
			if (props.pcProps.linkConfig && props.pcProps.linkConfig.length > 0) {
				const data: any = [];
				if (attr.multiple) {
					dictList.value.forEach((obj) => {
						if (val.includes(obj.value)) {
							data.push(obj);
						}
					});
				} else {
					data.push(dictList.value.find((obj) => obj.value === val));
				}
				const formDatas = getCheckRowsToFormDatas(props.pcProps.linkConfig, flatWigetDatas.value, data);
				setFieldsValue && setFieldsValue(formDatas);
			}
		}
	};

	const remoteMethod = (query: string) => {
		if (props.pcProps.isRemote && props.pcProps.onRemoteScript && scriptConfig) {
			(scriptConfig as Script).runScript(
				props.pcProps.code as string,
				'onRemoteScript',
				props.pcProps.onRemoteScript,
				query,
			);
		}
	};

	watch(
		[
			() => props.pcProps.citeType,
			() => props.pcProps.objectType,
			() => props.pcProps.objectServer,
			() => props.pcProps.objectValue,
			() => props.pcProps.objectLabel,
		],
		([newCiteType, newObjectType, newObjectServer, newObjectLabel, newObjectValue]) => {
			if (props.pcProps.paramsType === paramsTypeEnum.引用) {
				newCiteType && getDictList(newCiteType);
			} else if (props.pcProps.paramsType === paramsTypeEnum.业务对象) {
				newObjectType &&
					newObjectServer &&
					newObjectValue &&
					newObjectLabel &&
					getDataProcessListData(newObjectType, newObjectServer);
			} else {
				dictList.value = props.pcProps.params;
			}
		},
		{
			immediate: true,
		},
	);
</script>

<style scoped>
	.select {
		width: 100%;
	}

	.show-value {
		display: flex;
		gap: 3px;
		flex-wrap: wrap;
	}
</style>
